import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';
import { cn } from '@/lib/utils';
import {
  useDeleteAgentChatWorkspaceMutation,
  useListAgentChatWorkspacesQuery,
} from '@/services/api/agentChatWorkspaceService';

import { AgentChatProvider } from '../agent-chat/context/AgentChatContext';
import { EditorProvider } from '../demands-v2/editor/context/editor-provider';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { DeleteWorkspaceConfirmation } from './DeleteWorkspaceConfirmation';
import { WorkspaceExhibits } from './exhibits/WorkspaceExhibits';
import { WorkspaceDocument } from './WorkspaceDetail';
import { TabType, WorkspaceSidebar } from './WorkspaceList';

interface AgentChatWorkspaceProps {
  matterId: string;
  firmId: string;
}

export const AgentChatWorkspace = ({
  matterId,
  firmId,
}: AgentChatWorkspaceProps) => {
  const { data: workspaces, isLoading } = useListAgentChatWorkspacesQuery({
    matterId,
  });

  const [deleteWorkspace] = useDeleteAgentChatWorkspaceMutation();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<string | null>(
    null,
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('document');

  const selectedWorkspace =
    workspaces?.find((w) => w.id === selectedWorkspaceId) || null;

  const handleWorkspaceSelect = (workspaceId: string) => {
    if (workspaceId === selectedWorkspaceId) {
      return;
    }

    setSelectedWorkspaceId(workspaceId);
    setActiveTab('document');
  };

  const handleCreateDialogOpen = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setIsCreateDialogOpen(false);
  };

  const handleDeleteConfirm = (workspaceId: string) => {
    setWorkspaceToDelete(workspaceId);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false);
    setWorkspaceToDelete(null);
  };

  const handleDeleteConfirmation = async () => {
    if (workspaceToDelete) {
      try {
        await deleteWorkspace({
          matterId,
          workspaceId: workspaceToDelete,
        }).unwrap();

        // If the deleted workspace was selected, clear the selection
        if (workspaceToDelete === selectedWorkspaceId) {
          setSelectedWorkspaceId(null);
        }
      } catch (error) {
        toast.error('Failed to delete workspace');
      } finally {
        setIsDeleteConfirmOpen(false);
        setWorkspaceToDelete(null);
      }
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Sidebar content
  const sidebarContent = (
    <WorkspaceSidebar
      workspaces={workspaces || []}
      isLoading={isLoading}
      selectedWorkspaceId={selectedWorkspaceId}
      onWorkspaceSelect={handleWorkspaceSelect}
      onCreateWorkspace={handleCreateDialogOpen}
      onDeleteWorkspace={handleDeleteConfirm}
      matterId={matterId}
      onChangeTab={setActiveTab}
      activeTab={activeTab}
    />
  );

  // Main content
  let mainContent = null;

  if (!selectedWorkspace) {
    mainContent = (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Heading level={2}>Welcome to Agent Chat Workspaces</Heading>
        <Text className="mt-4 text-gray-500 max-w-md">
          Select a workspace from the list to view its details, or create a new
          one to get started.
        </Text>
      </div>
    );
  } else {
    const content = (
      <>
        <WorkspaceDocument
          workspace={selectedWorkspace}
          matterId={matterId}
          firmId={firmId}
          className={activeTab === 'document' ? '' : 'hidden!'}
        />
        <WorkspaceExhibits
          workspace={selectedWorkspace}
          matterId={matterId}
          className={activeTab === 'exhibits' ? '' : 'hidden!'}
        />
      </>
    );
    mainContent = content;
  }

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div
        className={`transition-all duration-300 ease-in-out border-r h-full overflow-auto ${
          isSidebarCollapsed ? 'w-0 opacity-0' : 'w-64 opacity-100'
        }`}
      >
        {sidebarContent}
      </div>

      {/* Sidebar toggle button */}
      <div className="relative">
        <Button
          variant="outline"
          size="icon"
          className={cn(
            'absolute top-12 h-6 w-6 rounded-full border shadow-md z-1',
            isSidebarCollapsed ? 'left-1' : '-left-3',
          )}
          onClick={toggleSidebar}
          aria-label={
            isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
          }
        >
          {isSidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Main content area */}
      <div className="flex flex-col flex-1 h-full overflow-hidden">
        {/* Content */}
        <EditorProvider>
          <AgentChatProvider>
            <div className="flex-1 overflow-auto">{mainContent}</div>
          </AgentChatProvider>
        </EditorProvider>
      </div>

      <CreateWorkspaceDialog
        isOpen={isCreateDialogOpen}
        onClose={handleCreateDialogClose}
        matterId={matterId}
      />

      <DeleteWorkspaceConfirmation
        isOpen={isDeleteConfirmOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirmation}
        workspaceId={workspaceToDelete}
        workspaces={workspaces || []}
      />
    </div>
  );
};
